// Verify email — handles the ?token= from the Keycast verification link.
// Calls Keycast to confirm, then redirects to sign-in.

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { SynvyaMark } from '@synvya/portal-ui/branding'

export function VerifyEmailPage() {
  const { t } = useTranslation('auth')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorMsg(t('verifyNoToken'))
      return
    }

    // Call Keycast verify endpoint — no cookies needed, token is sufficient.
    // credentials: 'omit' avoids CORS preflight issues across origins.
    const keycastUrl = (import.meta.env.VITE_KEYCAST_URL as string).replace(/\/+$/, '')
    fetch(`${keycastUrl}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'omit',
    })
      .then(async (res) => {
        if (res.ok) {
          setStatus('success')
          setTimeout(() => navigate('/sign-in'), 3000)
        } else {
          let msg = t('verifyFailed')
          try {
            const body = await res.json()
            msg = body?.message ?? body?.error?.message ?? msg
          } catch { /* non-JSON response */ }
          setStatus('error')
          setErrorMsg(`${msg} (HTTP ${res.status})`)
        }
      })
      .catch((err) => {
        setStatus('error')
        setErrorMsg(`${t('verifyFailed')} ${err instanceof Error ? err.message : ''}`.trim())
      })
  }, [token, navigate, t])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6">
      {/* Synvya mark */}
      <span className="mb-8 flex items-center justify-center">
        <SynvyaMark variant="green" size={48} title="Synvya" />
      </span>

      {status === 'loading' && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('verifyingEmail')}</p>
        </div>
      )}

      {status === 'success' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-400" />
          <p className="text-sm font-semibold text-foreground">{t('verifySuccess')}</p>
          <p className="text-sm text-muted-foreground">{t('verifyRedirect')}</p>
        </div>
      )}

      {status === 'error' && (
        <div className="flex flex-col items-center gap-3 text-center">
          <XCircle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-semibold text-foreground">{t('verifyFailedTitle')}</p>
          <p className="text-sm text-muted-foreground">{errorMsg}</p>
          <button
            onClick={() => navigate('/sign-in')}
            className="mt-2 text-sm font-medium text-primary hover:underline"
          >
            {t('signInLink')}
          </button>
        </div>
      )}
    </div>
  )
}
